import { use, useCallback, useState } from 'react';

import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import { EmptyState } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import ExportForm, {
	getExportFormatExtension,
	type ExportFormat,
	type ExportStatus,
} from '~components/forms/exportForm';
import { generalInformation, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import { DeviceMultiAssignDialog, DevicesDataGrid } from '~features/devices';
import DeviceMultiUnassignDialog from '~features/devices/components/dialogs/deviceMultiUnassignDialogs';
import SelectedDevicesContext from '~features/devices/providers/selectedDevicesContext';
import { ResourceGroupService } from '~features/resourceGroups';
import useAuthorize from '~hooks/useAuthorize';
import { OffsetPagedResults } from '~interfaces';
import Device from '~interfaces/device';
import ExportService from '~services/exportService';
import { getService } from '~services/serviceFactory';

import OrganisationActionBar from '../components/organisationActionBar';
import OrganisationContext from '../providers/organisationDetailsContext';
import OrganisationsService from '../services/organisationsService';

type UserAction = {
	type: 'assignDevices' | 'unAssignDevices' | 'export' | 'changeResourceGroupForDevices';
	mutate?: KeyedMutator<OffsetPagedResults<Device>>;
	clearSelection?: () => void;
} | null;

const organisationService = getService(OrganisationsService);
const resourceGroupService = getService(ResourceGroupService);
const exportService = getService(ExportService);

const OrganisationDevicesContainer = () => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [userAction, setUserAction] = useState<UserAction | null>(null);
	const [exportStatus, setExportStatus] = useState<ExportStatus>('selecting');
	const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null);
	const [showModalLoader, setShowModalLoader] = useState(false);
	const [searchParams] = useSearchParams();
	const organisationContext = use(OrganisationContext);
	const selectedDevicesContext = use(SelectedDevicesContext);
	const organisationId = organisationContext.organisation?.id ?? '0';
	const allowsExporting = useAuthorize().authorize([
		UserRoleEnum.SuperAdmin,
		UserRoleEnum.Support,
		UserRoleEnum.Manufacturing,
		UserRoleEnum.ClientAdmin,
	]);

	const clearUserAction = () => {
		setUserAction(null);
		selectedDevicesContext.updateSingleDevice(undefined);
		setTimeout(() => {
			setExportStatus('selecting');
			setSelectedFormat(null);
		}, 300);
	};

	const handleAssign = (
		mutate: KeyedMutator<OffsetPagedResults<Device>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'assignDevices',
			clearSelection,
			mutate: mutate,
		});
	};

	const handleUnassign = (
		mutate: KeyedMutator<OffsetPagedResults<Device>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'unAssignDevices',
			clearSelection,
			mutate: mutate,
		});
	};

	const handleChangeResourceGroupForDevices = (
		mutate: KeyedMutator<OffsetPagedResults<Device>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'changeResourceGroupForDevices',
			clearSelection,
			mutate: mutate,
		});
	};

	const handleUnassignDevices = async () => {
		setShowModalLoader(true);

		const deviceIds =
			selectedDevicesContext?.singleDevice ?
				[selectedDevicesContext?.singleDevice.id]
			:	(selectedDevicesContext?.selectedDevices?.flatMap((value) => value.id) ?? []);
		const isSingleDevice = deviceIds.length == 1;
		const successValue =
			isSingleDevice ?
				t('deviceUnassignedSuccesfully', { ns: 'toast' })
			:	t('devicesUnassignedSuccesfully', { ns: 'toast' });
		const failedValue =
			isSingleDevice ?
				t('deviceFailedToUnassign', { ns: 'toast' })
			:	t('devicesFailedToUnassign', { ns: 'toast' });

		await organisationService
			.unassignDevices(deviceIds)
			.then(() => {
				enqueueSnackbar(successValue, { variant: 'success' });
				cleanUpAfterDialog();
			})
			.catch((_reason) => {
				enqueueSnackbar(failedValue, { variant: 'error' });
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const handleAssignDevices = (
		organisationId: string,
		resourceGroupId: string | undefined,
		closeDialog: () => void
	) => {
		const deviceIds =
			selectedDevicesContext?.singleDevice ?
				[selectedDevicesContext?.singleDevice.id]
			:	(selectedDevicesContext?.selectedDevices?.flatMap((value) => value.id) ?? []);
		const isSingleDevice = deviceIds.length == 1;

		const successValue =
			isSingleDevice ?
				t('deviceAssignedSuccesfully', { ns: 'toast' })
			:	t('devicesAssignedSuccesfully', { ns: 'toast' });
		const failedValue =
			isSingleDevice ?
				t('deviceFailedToAssign', { ns: 'toast' })
			:	t('devicesFailedToAssign', { ns: 'toast' });

		if (!deviceIds || !organisationId) {
			enqueueSnackbar(failedValue, { variant: 'error' });
			return;
		}

		setShowModalLoader(true);
		if (userAction?.type === 'changeResourceGroupForDevices') {
			if (!resourceGroupId) {
				enqueueSnackbar(failedValue, { variant: 'error' });
				setShowModalLoader(false);
				return;
			}

			resourceGroupService
				.assignDevicesToResourceGroup(deviceIds, resourceGroupId, organisationId)
				.then(() => {
					cleanUpAfterDialog(closeDialog);
					enqueueSnackbar(successValue, { variant: 'success' });
				})
				.catch((_reason) => {
					enqueueSnackbar(failedValue, { variant: 'error' });
				})
				.finally(() => {
					setShowModalLoader(false);
				});
			return;
		}

		organisationService
			.assignDevicesToOrganisation(deviceIds, organisationId)
			.then(() => {
				if (resourceGroupId) {
					resourceGroupService
						.assignDevicesToResourceGroup(deviceIds, resourceGroupId, organisationId)
						.then(() => {
							cleanUpAfterDialog(closeDialog);
							enqueueSnackbar(successValue, { variant: 'success' });
						})
						.catch((_reason) => {
							enqueueSnackbar(failedValue, { variant: 'error' });
						});
				} else {
					enqueueSnackbar(successValue, { variant: 'success' });
					cleanUpAfterDialog(closeDialog);
				}
			})
			.catch((_reason) => {
				enqueueSnackbar(t('failedToAssignToOrganisation', { ns: 'errors' }), { variant: 'error' });
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const cleanUpAfterDialog = (closeDialog?: () => void) => {
		setUserAction(null);
		userAction?.mutate?.();
		userAction?.clearSelection?.();
		selectedDevicesContext.updateSingleDevice(undefined);
		closeDialog?.();
	};

	function triggerBlobDownload(blob: Blob, fileName: string) {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	const handleExport = useCallback(
		async (format: ExportFormat) => {
			setExportStatus('exporting');

			try {
				const search = searchParams.get(queryParameters.SearchQuery) ?? undefined;
				const model = searchParams.get(queryParameters.DeviceModelName) ?? undefined;
				const resourceGroupId = searchParams.get(queryParameters.ResourceGroupId) ?? undefined;

				const blob = await exportService.exportDevices({
					format,
					search,
					model,
					resourceGroupId,
					clientId: organisationId,
				});

				const fileName = `devices-export_${dayjs().format('DD-MM-YYYY_HH-mm')}${getExportFormatExtension(format)}`;
				triggerBlobDownload(blob, fileName);

				enqueueSnackbar(t('exportGeneratedSuccessfully', { ns: 'toast' }), { variant: 'success' });
			} catch (error) {
				console.error('Export failed:', error);
				enqueueSnackbar(t('exportFailedToGenerate', { ns: 'toast' }), { variant: 'error' });
			} finally {
				clearUserAction();
			}
		},
		[searchParams, organisationId, enqueueSnackbar, t]
	);

	return (
		<>
			{organisationId != undefined ?
				<>
					<OrganisationActionBar organisationId={organisationId} showEditButton={false} />
					<DevicesDataGrid
						showTitle={true}
						showExportButton={allowsExporting}
						listType={'assigned'}
						enableDeleteDevice
						enableEditDevice
						enableMultiSelect
						enableUnassignDevice={organisationId != generalInformation.manufacturingOrganisationId}
						enableAssignDevice={organisationId == generalInformation.manufacturingOrganisationId}
						overwrittenClientId={organisationId}
						onAssignDevices={handleAssign}
						onUnassignDevices={handleUnassign}
						onChangeResourceGroupForDevices={handleChangeResourceGroupForDevices}
						includeResourceGroup={true}
						handleExportButtonClicked={() => setUserAction({ type: 'export' })}
					/>
				</>
			:	<EmptyState
					icon={'icon/bar-chart-square'}
					title={t('device.status.noHeartbeatsYet', { ns: 'entities' })}
					description='There is no telemetry data available for this device'
				/>
			}
			<DeviceMultiAssignDialog
				open={
					userAction?.type === 'assignDevices' ||
					userAction?.type === 'changeResourceGroupForDevices'
				}
				currentlyAssignedOrganisation={
					userAction?.type === 'changeResourceGroupForDevices' ?
						organisationContext.organisation
					:	undefined
				}
				showEditOrganisation={userAction?.type === 'assignDevices'}
				loading={showModalLoader}
				onClose={clearUserAction}
				onFinish={handleAssignDevices}
			/>
			<DeviceMultiUnassignDialog
				open={userAction?.type === 'unAssignDevices'}
				loading={showModalLoader}
				onClose={clearUserAction}
				onFinish={handleUnassignDevices}
			/>
			<FormDialog
				open={userAction?.type === 'export'}
				title={
					t('export', { ns: 'actions' }) +
					' ' +
					t('device.labels.plural', { ns: 'entities' }).toLowerCase()
				}
				loading={exportStatus === 'exporting'}
				submitLabel={t('export', { ns: 'actions' })}
			>
				<ExportForm
					status={exportStatus}
					selectedFormat={selectedFormat}
					onFormatChange={setSelectedFormat}
					onSubmit={handleExport}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default OrganisationDevicesContainer;
